import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { duration, Icon } from './ui';

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

/**
 * Plays one recording with a waveform: stereo recordings show the caller
 * (left channel) and the agent (right) as two lanes. Click to jump, pick a
 * speed, or download the file.
 */
export function RecordingPlayer({ recordingId, title }: { recordingId: string; title: string }) {
  const [src, setSrc] = useState<{ url: string; peaks: number[][]; ext: 'wav' | 'opus' } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [time, setTime] = useState(0);
  const [length, setLength] = useState(0);
  const audio = useRef<HTMLAudioElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let live = true;
    let objectUrl = '';
    setSrc(null);
    setError(null);
    (async () => {
      const { url } = await api<{ url: string }>('GET', `/recordings/${recordingId}/url`);
      const res = await fetch(url);
      if (!res.ok) throw new Error(res.status === 404 ? 'The recording file is not available yet.' : 'Could not load the recording.');
      const bytes = await res.arrayBuffer();
      // WAV, or Ogg Opus once the recording is compressed
      const type = res.headers.get('content-type') ?? 'audio/wav';
      objectUrl = URL.createObjectURL(new Blob([bytes], { type }));
      const ctx = new AudioContext();
      const decoded = await ctx.decodeAudioData(bytes.slice(0)).finally(() => void ctx.close());
      const buckets = 600;
      const peaks = [...Array(decoded.numberOfChannels)].map((_, c) => {
        const data = decoded.getChannelData(c);
        const step = Math.max(1, Math.floor(data.length / buckets));
        return [...Array(buckets)].map((__, b) => {
          let peak = 0;
          for (let i = b * step; i < Math.min(data.length, (b + 1) * step); i++) peak = Math.max(peak, Math.abs(data[i]));
          return peak;
        });
      });
      if (live) {
        setSrc({ url: objectUrl, peaks, ext: type.includes('ogg') ? 'opus' : 'wav' });
        setLength(decoded.duration);
      }
    })().catch((err) => live && setError((err as Error).message));
    return () => {
      live = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setPlaying(false);
      setTime(0);
    };
  }, [recordingId]);

  // Redraw the waveform with the played part highlighted
  useEffect(() => {
    const c = canvas.current;
    if (!c || !src) return;
    const dpr = window.devicePixelRatio || 1;
    const w = c.clientWidth;
    const h = c.clientHeight;
    c.width = w * dpr;
    c.height = h * dpr;
    const g = c.getContext('2d')!;
    g.scale(dpr, dpr);
    const css = getComputedStyle(c);
    const colors = [css.getPropertyValue('--color-ion'), css.getPropertyValue('--color-oncall')];
    const dim = css.getPropertyValue('--color-line-strong');
    const lanes = src.peaks.length;
    const laneH = h / lanes;
    const done = length ? time / length : 0;
    const bars = src.peaks[0].length;
    const bw = w / bars;
    src.peaks.forEach((peaks, lane) => {
      const mid = laneH * lane + laneH / 2;
      const loudest = Math.max(0.05, ...peaks);
      peaks.forEach((p, i) => {
        const bh = Math.max(1, (p / loudest) * (laneH / 2 - 4));
        g.fillStyle = i / bars <= done ? colors[lane % 2] : dim;
        g.fillRect(i * bw, mid - bh, Math.max(1, bw - 0.5), bh * 2);
      });
    });
  }, [src, time, length]);

  useEffect(() => {
    if (audio.current) audio.current.playbackRate = speed;
  }, [speed, src]);

  function seek(e: React.MouseEvent<HTMLCanvasElement>) {
    const a = audio.current;
    if (!a || !length) return;
    const r = e.currentTarget.getBoundingClientRect();
    a.currentTime = ((e.clientX - r.left) / r.width) * length;
    setTime(a.currentTime);
  }

  const toggle = () => {
    const a = audio.current;
    if (!a) return;
    if (a.paused) void a.play();
    else a.pause();
  };

  return (
    <div className="rounded-xl border border-line bg-hull/60 p-4">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={!src}
          onClick={toggle}
          aria-label={playing ? 'Pause' : 'Play'}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-ion text-on-accent transition hover:brightness-110 disabled:opacity-40"
        >
          <Icon name={playing ? 'pause' : 'play'} size={16} />
        </button>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{title}</p>
          <p className="tabular text-xs text-ink-3">
            {duration(Math.floor(time))} / {duration(Math.round(length))}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-1" role="group" aria-label="Playback speed">
          {SPEEDS.map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={speed === s}
              onClick={() => setSpeed(s)}
              className={`tabular rounded-md px-2 py-1 text-xs ${speed === s ? 'bg-ion/15 text-ink' : 'text-ink-3 hover:text-ink'}`}
            >
              {s}×
            </button>
          ))}
        </div>
        {src ? (
          <a href={src.url} download={`${title.replace(/[^\w.-]+/g, '_')}.${src.ext}`} className="text-xs text-ink-2 underline-offset-2 hover:text-ink hover:underline">
            Download
          </a>
        ) : null}
      </div>
      {error ? (
        <p className="py-6 text-center text-sm text-break">{error}</p>
      ) : !src ? (
        <p className="py-6 text-center text-sm text-ink-3">Loading the recording…</p>
      ) : (
        <div className="flex gap-3">
          {src.peaks.length === 2 ? (
            <div className="flex w-12 shrink-0 flex-col justify-around text-[11px] text-ink-3">
              <span>Caller</span>
              <span>Agent</span>
            </div>
          ) : null}
          <canvas
            ref={canvas}
            onClick={seek}
            aria-label="Waveform: click to jump"
            className={`w-full cursor-pointer ${src.peaks.length === 2 ? 'h-28' : 'h-16'}`}
          />
        </div>
      )}
      {src ? (
        <audio
          ref={audio}
          src={src.url}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
          className="hidden"
        />
      ) : null}
    </div>
  );
}
